import { useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import CalendarWidget from "./CalendarWidget";
import ScheduleWidget from "./ScheduleWidget";
import ScoreWidget from "./ScoreWidget";
import TrackerWidget from "./TrackerWidget";
import Card from "../../../components/Card/Card";
import ImageWithText from "../../../components/ImageWithText/ImageWithText";
import Button from "../../../components/Button/Button";
import { AddOutlined, CloseOutlined, EmailOutlined } from "@material-ui/icons";
import Avatar from "../../../components/Avatar/Avatar";
import { enumToLookup } from "../../../../models/lookup.model";
import IconButton from "../../../components/IconButton/IconButton";
import { Box, Drawer, Grid, Link, Toolbar, Typography } from "@material-ui/core";
import PageContainer from "../../../components/Page/PageContainer";
import RouterHashModal, { RenderRouterHashModalProps } from "../../RouterHashModal/RouterHashModal";
import { useHistory } from "react-router-dom";
import PageHeader from "../../../components/PageHeader/PageHeader";
import Widget from "../../../components/Svg/Widget";
import { DashboardWidget, useUserSettingsContext } from "../../../../context/user-settings.context";
import OverdueTestWidget from './OverdueTestWidget';

const ADD_WIDGET_HASH = "#widgets";

const useStyles = makeStyles((theme) => ({
  page: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
  },
  dashboard: {
    // Grid layout via flexbox
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    margin: `calc(${theme.spacing(2)}px * -0.75)`,

    /*"@supports (display: grid)": {
      display: "grid",
      gridGap: theme.spacing(2),
      gridTemplateColumns: "repeat(3, 1fr)",

      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, 1fr)",
      },
    },*/
  },
  // Outermost row (calendarColumn + scheduleColumn). Unlike `dashboard`, this has NO negative
  // margin - its direct children aren't `.widget`s with a canceling border, so that trick would
  // just push this row 12px past the page's padding on both sides instead of aligning with it
  // (and with the search bar above, which sits flush with that same page padding).
  columns: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },

  widget: {
    position: "relative",
    border: `calc(${theme.spacing(2)}px * 0.75) solid transparent`,
    minWidth: "160px",
    width: "calc(200% / 3)",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  smallWidget: {
    width: "calc(100% / 3)",

    [theme.breakpoints.down("sm")]: {
      width: "50%",
    },
  },
  largeWidget: {
    width: "100%",
  },
  halfWidget: {
    width: "50%",
  },
  // Score and Tracker sit side by side - shrink just the border facing each other (leaving
  // the outer-facing border alone so their outer edges still align with the row above/below).
  scoreWidget: {
    borderRightWidth: `calc(${theme.spacing(2)}px * 0.4)`,
  },
  trackerWidget: {
    borderLeftWidth: `calc(${theme.spacing(2)}px * 0.4)`,
  },
  // Column holding Calendar (top) and the Score/Tracker row (bottom) - sized so it and
  // scheduleColumn (~28.6%) add up to the full dashboard width.
  calendarColumn: {
    display: "flex",
    flexDirection: "column",
    flex: "3 1 0%",
    minWidth: "320px",
    marginRight: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      flex: "1 1 100%",
      marginRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  // Column holding just ZapThruIT. It's a flex sibling of calendarColumn in a
  // stretch-aligned row, so it naturally stretches to match calendarColumn's full height
  // (Calendar row + Score/Tracker row combined) - that's what makes ZapThruIT "full height".
  scheduleColumn: {
    display: "flex",
    flex: "1.2 1 0%",
    minWidth: "160px",

    [theme.breakpoints.down("sm")]: {
      flex: "1 1 100%",
    },
  },
  scheduleWidget: {
    width: "100%",
    height: "100%",

    // Once the columns stack (tablet and below) scheduleColumn no longer stretches to
    // calendarColumn's height, so "100%" resolves to nothing - and the ZapThruIT list is a
    // virtualized (react-virtuoso) list that renders no rows without a real height. Give it one.
    [theme.breakpoints.down("sm")]: {
      height: "600px",
    },
  },
  // scheduleColumn is a row-direction flex container, so unlike calendarColumn (column-direction,
  // where cross-axis stretch already forces full width for free) its single child - the
  // gutter-canceling .dashboard wrapper around the ZapThruIT widget - only shrink-wraps to its
  // list content's natural width by default. This forces it to fill the column's width instead,
  // which is what makes the ZapThruIT card actually reach the column's right edge.
  scheduleRow: {
    flex: "1 1 auto",
  },
  // The Calendar row and the Score/Tracker row are each their own gutter-canceling
  // .dashboard wrapper (so their contents align edge-to-edge with the rest of the page -
  // see calendarColumn), which means their negative margins cancel each other out where they
  // meet and leave zero visible gap between the two rows. This restores that gap, matching
  // the horizontal gutter between side-by-side widgets (2x the widget border width).
  calendarRow: {
    marginBottom: `calc(${theme.spacing(2)}px * 0.25)`,
  },
  widgetButton: {
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    background: "transparent",
    "&:hover": {
      background: "transparent",
    },
  },
}));

function Home() {
  const classes = useStyles();
  const history = useHistory();
  const { userSettings, setUserSettings, dashboardSettings, setDashboardSettings } = useUserSettingsContext();

  const dashboardWidgets = Object.values(DashboardWidget);

  // Widget state
  const selectedWidgets = dashboardSettings?.widgets || dashboardWidgets;

  const availableWidgets = dashboardWidgets.filter((x) => !selectedWidgets.includes(x));

  const handleOpenMenu = useCallback(
    (hash: string) => () => {
      history.push(`${hash}`);
    },
    [history]
  );

  const handleRemoveWidget = useCallback(
    (widget: DashboardWidget) => () => {
      setDashboardSettings((prev) => ({ ...prev, widgets: [...(prev?.widgets || []).filter((w) => w !== widget)] }));
    },
    [setDashboardSettings]
  );
  const handleAddWidget = useCallback(
    (widget: DashboardWidget) => () => {
      setDashboardSettings((prev) => ({ ...prev, widgets: [...(prev?.widgets || []).filter((w) => w !== widget), widget] }));
      history.push(`/`);
    },
    [history, setDashboardSettings]
  );

  const renderAddWidgetModal = useCallback(
    (props: RenderRouterHashModalProps) => {
      return (
        <Drawer
          open={props.open}
          onClose={props.onClose}
          anchor="bottom"
          PaperProps={{
            style: { top: "10%" },
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          <PageContainer>
            <Grid container direction="column">
              <Grid item>
                <PageHeader id={"add-widget-header"} header={`Add dashboard widgets`}>
                  <Grid container justify="flex-end" alignItems="center">
                    <Grid item>
                      <IconButton onClick={props.onClose} edge="right">
                        <CloseOutlined />
                      </IconButton>
                    </Grid>
                  </Grid>
                </PageHeader>
              </Grid>

              {props.open && (
                <Grid item xs={12}>
                  <div className={classes.dashboard}>
                    {availableWidgets.map((item, index) => (
                      <div
                        key={item}
                        className={clsx(
                          classes.widget,
                          item === DashboardWidget.Schedule
                            ? classes.smallWidget
                            : item === DashboardWidget.Score
                              ? classes.smallWidget
                              : item === DashboardWidget.Tracker
                                ? classes.smallWidget
                                : null
                        )}
                      >
                        {item === DashboardWidget.Calendar ? (
                          <CalendarWidget style={{ opacity: 0.4 }} />
                        ) : item === DashboardWidget.Schedule ? (
                          <ScheduleWidget style={{ opacity: 0.4 }} />
                        ) : item === DashboardWidget.Score ? (
                          <ScoreWidget style={{ opacity: 0.4 }} />
                        ) : item === DashboardWidget.Tracker ? (
                          <TrackerWidget style={{ opacity: 0.4 }} />
                        ) : null}

                        <Box position="absolute" width="100%" height="100%" top={0} left={0}>
                          <div className={classes.widgetButton}>
                            <Button
                              color="secondary"
                              variant="contained"
                              elevation
                              startIcon={<AddOutlined />}
                              onClick={handleAddWidget(item as DashboardWidget)}
                            >
                              Add {Object.values(DashboardWidget)[index]}
                            </Button>
                          </div>
                        </Box>
                      </div>
                    ))}
                  </div>
                </Grid>
              )}
            </Grid>
          </PageContainer>
        </Drawer>
      );
    },
    [availableWidgets, classes.dashboard, classes.smallWidget, classes.widget, classes.widgetButton, handleAddWidget]
  );

  return (
    <>
      <div className={classes.page}>
        <div className={classes.columns}>
          <div className={classes.calendarColumn}>
            {!selectedWidgets.includes(DashboardWidget.Calendar) ? null : (
              <div className={clsx(classes.dashboard, classes.calendarRow)}>
                <div className={clsx(classes.widget, classes.largeWidget)}>
                  <CalendarWidget /*onRemoveWidget={handleRemoveWidget(DashboardWidget.Calendar)}*/ />
                </div>
              </div>
            )}

            <div className={classes.dashboard}>
              {!selectedWidgets.includes(DashboardWidget.Score) ? null : (
                <div className={clsx(classes.widget, classes.halfWidget, classes.scoreWidget)}>
                  <ScoreWidget /*onRemoveWidget={handleRemoveWidget(DashboardWidget.Score)}*/ />
                </div>
              )}

              {!selectedWidgets.includes(DashboardWidget.Tracker) ? null : (
                <div className={clsx(classes.widget, classes.halfWidget, classes.trackerWidget)}>
                  <TrackerWidget /*onRemoveWidget={handleRemoveWidget(DashboardWidget.Tracker)}*/ />
                </div>
              )}
            </div>
          </div>

          {!selectedWidgets.includes(DashboardWidget.Schedule) ? null : (
            <div className={classes.scheduleColumn}>
              <div className={clsx(classes.dashboard, classes.scheduleRow)}>
                <div className={clsx(classes.widget, classes.scheduleWidget)}>
                  <ScheduleWidget /*onRemoveWidget={handleRemoveWidget(DashboardWidget.Schedule)}*/ />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <RouterHashModal hash={ADD_WIDGET_HASH} previousLocation={`/`} renderModal={renderAddWidgetModal} />
    </>
  );
}

export default Home;
